/**
 * The mentions that survive closing the tab, and the one way this product is
 * allowed to interrupt somebody.
 *
 * A room keeps its last thousand messages and a socket keeps nothing at all,
 * so until now "somebody answered you" was news that expired when the tab
 * did. The inbox is the other half: when a message names a tag, the Worker
 * writes a row against the account behind that tag (docs/contracts.md, "The
 * chat"), and this is the client for the two routes that read and clear them.
 * It is per account and not per browser, because a mention is addressed to a
 * person rather than to a machine, and the account is the only thing this
 * product has that is a person.
 *
 * Two rules shape the file.
 *
 * 1. **Nothing here may break the chat.** Both routes are new, both are on a
 *    Worker that may not be deployed, and one of them needs a session that
 *    may have expired an hour ago. Every call answers with an empty or
 *    neutral result instead of throwing, so a tab talking to a Worker that
 *    has never heard of `/api/chat/inbox` behaves exactly like a tab whose
 *    reader has no mentions. That is the same rule the rest of the chat
 *    already keeps — the app runs unchanged when the Worker is unreachable —
 *    and it matters more here than anywhere, because this is the one part of
 *    the feature a signed-out reader never even asks for.
 *
 * 2. **The notification is local, and it is a courtesy.** It is a
 *    `Notification` raised by this page, with no push subscription, no
 *    service worker and nothing on a server that outlives the tab. That is a
 *    deliberate ceiling rather than a first step: a push subscription is an
 *    endpoint a third party holds, a set of VAPID keys somebody has to keep,
 *    and a promise to deliver mail after the learner has gone — three
 *    permanent obligations bought for one feature that is only worth having
 *    while the tab is open anyway. What this does buy is the case that
 *    actually happens: the panel is shut or the tab is in the background,
 *    somebody answers, and the reader finds out before they have moved on.
 *
 * Every `Notification` call is wrapped, including reading `Notification` at
 * all. It is absent on iOS Safari, absent in a page embedded where
 * notifications are partitioned away, and throws outright in some browsers
 * when constructed outside a user gesture — and a chat that crashes because a
 * notification failed is very much worse than a missed notification.
 */
import { LIMITS, cleanHistoricalTag, parseRoomId, roomLabel, type ChatLocale, type RoomId } from '@engineer-os/chat';
import { chatUrl } from './chat';

/** One mention, as the account remembers it. Shapes come from the Worker; ids count up. */
export interface InboxMention {
  /** The row's own id, which is what `seen` is recorded against. Monotonic across rooms. */
  id: number;
  room: RoomId;
  /** The message in that room, for a permalink back into the conversation. */
  messageId: number;
  ts: number;
  fromName: string;
  fromTag: string;
  text: string;
}

/**
 * How far the account has read one room, and when it last did.
 *
 * `updated` is the useful half. `lastRead` is a message id, and message ids
 * are room-local, so it can only be compared with other ids from the same
 * room — which is exactly the thing the tab cannot get without waking that
 * room. `updated` is a wall clock, and the room index already publishes a
 * wall clock per room, so the two can be compared without waking anything.
 */
export interface InboxMark {
  room: RoomId;
  lastRead: number;
  updated: number;
}

/** What one read of the inbox found. */
export interface InboxSnapshot {
  mentions: InboxMention[];
  /**
   * Where this account has read up to, one row per room it has ever read.
   *
   * The route deliberately does not answer with a count of what is waiting,
   * and the reason is worth keeping: the marks live beside the account in D1
   * and the message ids live inside each room's Durable Object, so counting
   * server-side would mean waking every room the reader has ever been in, on
   * a route a tab asks for on load. What the tab does instead is join these
   * against the room index (`GET /api/chat/rooms`), which is one cached read
   * that wakes nothing: a room whose last traffic is newer than the moment
   * this reader last read it has something new in it.
   *
   * That yields rooms-with-something-new rather than a message count, and
   * that is the honest number rather than a lesser one — nothing anywhere
   * knows how many messages a signed-out week contained.
   */
  marks: InboxMark[];
  /**
   * A count the server worked out, if it ever starts answering with one.
   *
   * Zero from the Worker as it stands. Kept because the field costs nothing
   * and because a later route that *can* count cheaply should not need this
   * file changed to be believed.
   */
  elsewhere: number;
}

const EMPTY: InboxSnapshot = { mentions: [], marks: [], elsewhere: 0 };

/**
 * `credentials: 'include'` rather than `'same-origin'`.
 *
 * Both routes are the session's, and the session is a cookie. On the normal
 * deployment the Worker serves this page and the two are identical; on a
 * static build pointed at a Worker elsewhere (`VITE_CHAT_ORIGIN`)
 * `'same-origin'` would silently send no cookie and the inbox would be
 * permanently empty for a reader who is signed in, with nothing on screen to
 * say why. `'include'` is the same request in the first case and the only
 * one that can work in the second.
 */
const SESSION: RequestInit = { credentials: 'include', cache: 'no-store' };

/**
 * The unseen mentions and the unread-elsewhere count, or an empty snapshot.
 *
 * Signed out is an empty snapshot and not an error: the route answers 404 for
 * a request with no session (the same "probing learns nothing" rule the admin
 * routes follow), so there is nothing here that can tell a signed-out reader
 * apart from a Worker that is not there, and nothing that needs to.
 */
export async function fetchInbox(signal?: AbortSignal): Promise<InboxSnapshot> {
  try {
    const r = await fetch(chatUrl('/api/chat/inbox').href, { ...SESSION, signal });
    if (!r.ok) return EMPTY;
    const body = (await r.json()) as { mentions?: unknown; elsewhere?: unknown; marks?: unknown };
    return { mentions: cleanMentions(body.mentions), marks: cleanMarks(body.marks), elsewhere: cleanElsewhere(body) };
  } catch {
    return EMPTY;
  }
}

/**
 * Acknowledge mentions: the ids given, or every one of them.
 *
 * Returns whether the server agreed, which the caller uses to decide whether
 * to advance the local high-water mark — not to decide whether to clear the
 * list. The list is cleared either way, because a reader who has looked at
 * their mentions has looked at them whatever a request did, and showing them
 * again on the next poll would be the tab arguing with its reader about what
 * they have read.
 */
export async function markSeen(ids?: number[]): Promise<boolean> {
  try {
    const r = await fetch(chatUrl('/api/chat/inbox/seen').href, {
      ...SESSION,
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // An absent `ids` means all of them. An empty array would mean none,
      // which is a request worth no round trip, so the caller is expected not
      // to make it — but sending `[]` is still honest if it does.
      body: JSON.stringify(ids ? { ids: ids.slice(0, LIMITS.inbox) } : {}),
    });
    return r.ok;
  } catch {
    return false;
  }
}

/* ── Reading what the Worker said ───────────────────────────────────────── */

/**
 * A list of mentions held to the same suspicion a stored blob is held to.
 *
 * The Worker is ours and the session is checked, so this is not a trust
 * boundary in the security sense; it is a *shape* boundary, and the shape on
 * the other side is being written by somebody else at the same time as this.
 * A row that does not parse is dropped rather than allowed to reach a
 * renderer as an undefined name, which is the failure that would look like a
 * bug in the panel rather than a gap in the route.
 */
function cleanMentions(raw: unknown): InboxMention[] {
  if (!Array.isArray(raw)) return [];
  const out: InboxMention[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const v = item as Record<string, unknown>;
    const room = parseRoomId(v.room);
    const tag = cleanHistoricalTag(v.fromTag);
    if (!room || !tag) continue;
    if (typeof v.id !== 'number' || typeof v.messageId !== 'number') continue;
    out.push({
      id: Math.floor(v.id),
      room,
      messageId: Math.floor(v.messageId),
      ts: typeof v.ts === 'number' ? v.ts : Date.now(),
      fromName: typeof v.fromName === 'string' ? v.fromName : '',
      fromTag: tag,
      text: typeof v.text === 'string' ? v.text : '',
    });
    if (out.length >= LIMITS.inbox) break;
  }
  // Newest first, whatever order the route chose. The panel draws them in
  // this order and the highest id is the one the seen mark moves to, so both
  // readers want the same sort and neither should have to do it.
  out.sort((a, b) => b.id - a.id);
  return out;
}

/** The read marks, checked row by row. A row naming a room that is not one is
 *  dropped rather than fatal: the whole answer is advisory. */
function cleanMarks(raw: unknown): InboxMark[] {
  if (!Array.isArray(raw)) return [];
  const out: InboxMark[] = [];
  for (const item of raw) {
    if (typeof item !== 'object' || item === null) continue;
    const v = item as Record<string, unknown>;
    const room = parseRoomId(v.room);
    if (!room) continue;
    if (typeof v.lastRead !== 'number' || typeof v.updated !== 'number') continue;
    out.push({ room, lastRead: Math.floor(v.lastRead), updated: v.updated });
  }
  return out;
}

/**
 * A count the server worked out, if it sent one.
 *
 * It does not, today, and the comment on {@link InboxSnapshot.elsewhere} says
 * why counting server-side would be the wrong trade. This stays because
 * believing a number the server *did* send should never require editing this
 * file, and because a route that can count cheaply may exist one day.
 */
function cleanElsewhere(body: { elsewhere?: unknown }): number {
  return typeof body.elsewhere === 'number' && body.elsewhere > 0 ? Math.floor(body.elsewhere) : 0;
}

/**
 * How many rooms have something in them this reader has not seen.
 *
 * The join the route deliberately did not do: a mark says when this account
 * last read a room, the index says when that room last had traffic, and a
 * room whose traffic is newer than the reading has something new. Neither
 * side of that comparison costs a room being woken.
 *
 * Rooms the tab is already holding a socket to are excluded, because those
 * have a live unread count of their own on the room strip and counting them
 * twice would mean the panel disagreeing with itself on screen.
 *
 * What this cannot be is a message count, and it is not pretending to be one:
 * nothing anywhere knows how many messages a signed-out week contained.
 */
export function roomsWithNews(
  marks: readonly InboxMark[],
  occupancy: Readonly<Record<string, { here: number; lastTs: number }>>,
  open: readonly RoomId[],
): number {
  let rooms = 0;
  for (const mark of marks) {
    if (open.includes(mark.room)) continue;
    const room = occupancy[mark.room];
    if (room && room.lastTs > mark.updated) rooms += 1;
  }
  return rooms;
}

/* ── The notification ───────────────────────────────────────────────────── */

/** Whether a notification could be raised right now without asking anybody anything. */
export function canNotify(): boolean {
  try {
    return typeof Notification !== 'undefined' && Notification.permission === 'granted';
  } catch {
    return false;
  }
}

/** Whether the browser has notifications at all, and has not already refused them. */
export function mayAskToNotify(): boolean {
  try {
    return typeof Notification !== 'undefined' && Notification.permission === 'default';
  } catch {
    return false;
  }
}

/**
 * Ask for permission, from a click and nowhere else.
 *
 * The caller is a toggle the reader has just pressed, which is the only
 * moment a permission prompt is an answer to a question they asked. It is
 * never called on load: a prompt on a page somebody has just opened is noise
 * they have no way to evaluate, and a refusal is permanent in most browsers,
 * so asking early is how a feature loses the readers who would have wanted
 * it. (`askToPersist` in the vault is not called on load for exactly this
 * reason; see docs/contracts.md, "2. The mirrors".)
 *
 * Returns whether notifications may now be raised. Older browsers hand back a
 * callback instead of a promise, hence the wrapper and the catch.
 */
export async function askToNotify(): Promise<boolean> {
  try {
    if (typeof Notification === 'undefined') return false;
    if (Notification.permission === 'granted') return true;
    if (Notification.permission === 'denied') return false;
    const answer = await Notification.requestPermission();
    return answer === 'granted';
  } catch {
    return false;
  }
}

/**
 * Somebody said your name. Raise it, or do not, and never make a noise about
 * failing to.
 *
 * The title is the person and the room and the body is what they said, in
 * that order and with no sentence around them, which is the one arrangement
 * that needs no new strings and reads the same in both languages: a name is a
 * name, `roomLabel` is already bilingual, and the message is the reader's
 * business rather than ours. The tag ties every notification for one room
 * together, so a room that is talking replaces its own notification instead
 * of stacking six of them.
 *
 * Nothing is returned. There is no useful answer: a browser that refused, a
 * platform with no notifications, and a page the operating system decided not
 * to disturb are the same outcome from here, and the mention is in the panel
 * either way.
 */
export function notifyMention(m: { fromName: string; room: RoomId; text: string }, locale: ChatLocale = 'en'): void {
  if (!canNotify()) return;
  try {
    const n = new Notification(`${m.fromName} · ${roomLabel(m.room, locale)}`, {
      body: m.text,
      tag: `engineeros-chat-${m.room}`,
      // Not `requireInteraction`: a notification that will not go away until
      // it is dismissed is a modal dialog the operating system is holding on
      // this product's behalf, and a chat message has not earned one.
      silent: false,
    });
    // Clicking it brings the tab forward, which is the whole of what it can
    // do — the panel is already showing the room by the time anybody gets
    // here, and a page that jumped somewhere on a click from outside would be
    // a page that moved while nobody was looking.
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch {
        /* a window that will not focus is still a notification that was read */
      }
    };
  } catch {
    /* constructed outside a gesture, or a platform that only pretends to have this */
  }
}
